// What an agent call was about (Console D's Agents page, APS-310): its audit row keeps the route it
// called (`GET /v1/projects/{p}/issues/iss_8kq2`), from which the Calls table links the issue,
// session or trace, and otherwise shows the rest of the path as text. A call's outcome is its
// HTTP status, from the row written once it answered (APS-300).

export type CallTarget =
  | { kind: 'issue' | 'session' | 'trace'; id: string }
  | { kind: 'path'; path: string }
  | { kind: 'none' }

const OBJECT = { issues: 'issue', sessions: 'session', traces: 'trace' } as const
const ID = /^[A-Za-z0-9._:-]{1,128}$/

/** The issue, session or trace a call read or changed, or what it called, from its route. */
export function callTarget(route: string): CallTarget {
  const path = route.replace(/^[A-Z]+ /, '')
  const rest = path.replace(/^\/v1\/projects\/[^/]+\/?/, '').replace(/^\/v1\/?/, '')
  if (!rest) return { kind: 'none' }
  const [what, id, more] = rest.split('/')
  const kind = OBJECT[what as keyof typeof OBJECT]
  if (kind && id && !more) {
    const decoded = safeDecode(id)
    if (ID.test(decoded)) return { kind, id: decoded }
  }
  return { kind: 'path', path: `/${rest}` }
}

function safeDecode(s: string): string {
  try {
    return decodeURIComponent(s)
  } catch {
    return s
  }
}

/** The method and path of a call made without the MCP server, for its Call column: the API's
 * route without the project, which the page is about. */
export function callRoute(route: string): string {
  return route.replace(/\/projects\/[^/]+/, '')
}

export type CallOutcome = 'ok' | 'refused' | 'failed' | 'pending'

/** answered 2xx or 3xx; refused 4xx (a grant, a scope, a limit); failed 5xx; no status yet. */
export function callOutcome(status: number | null | undefined): CallOutcome {
  if (status === null || status === undefined) return 'pending'
  if (status < 400) return 'ok'
  return status < 500 ? 'refused' : 'failed'
}

/** The change from one count to another, in whole percent; null when there was nothing before. */
export function change(before: number, now: number): number | null {
  if (before <= 0) return null
  return Math.round(((now - before) / before) * 100)
}

/** Within the last five minutes: an agent's last call that makes it "active now". */
export const ACTIVE_MS = 5 * 60_000
export const isActive = (at: string, now: Date) =>
  now.getTime() - new Date(at).getTime() < ACTIVE_MS
